import type { CivilDate } from '@hue/design-system';
import { z } from 'zod';
import type {
  AuditDirection,
  AuditOriginFilter,
  AuditSort,
  ContributionDirection,
  ContributionSort,
  ContributionStatusFilter,
  ContributionTypeFilter,
  EmailMessageSort,
  EmailMessageStatus,
  EntryDirection,
  EntryPeriodBy,
  EntrySort,
  EntryStatus,
  PaymentMethod,
  PartnerActivityFilter,
  PartnerDirection,
  PartnerSort,
  ClientAccessFilter,
  ClientActivityFilter,
  ClientDirection,
  ClientKind,
  ClientSort,
  Currency,
  ServiceActivityFilter,
  ServiceDirection,
  ServiceSort,
  InventoryActivityFilter,
  InventoryDirection,
  InventoryLotTrackingFilter,
  InventoryMovementFlow,
  InventoryMovementKind,
  InventoryMovementSort,
  InventoryProductSort,
  PurchasingActivityFilter,
  PurchasingDirection,
  PurchasingKindFilter,
  PurchasingOfferingKindFilter,
  SupplierOfferingSort,
  SupplierSort,
  AppointmentSort,
  AppointmentStatusFilter,
  ScheduleBlockSort,
  SchedulingActivityFilter,
  SchedulingDirection,
  ProtocolActivityFilter,
  ProtocolDirection,
  ProtocolSort,
  FinanceAccountActivity,
  FinanceAccountKind,
  FinanceMovementDirection,
  FinanceMovementSort,
  FinanceMovementStatus,
  FinancialObligationSort,
  FinancialObligationStatusChoice,
  ReconciliationState,
} from '../lib/api.js';
import {
  choicesSearch,
  stringChoicesSearch,
  civilDateSearch,
  pageOffsetSearch,
  pageSizeSearch,
  sortDirectionSearch,
} from './view-state.js';

/// O contrato de cada lista com a barra de endereço, num lugar só.
///
/// Mora fora das telas por um motivo prático — arquivo que exporta componente **e** constante
/// quebra o recarregamento a quente, e o lint trata isso como erro — e por um motivo melhor: assim
/// dá para ler de uma vez o que o sistema inteiro aceita na URL, que é superfície pública.
///
/// Cada esquema termina em `.partial()`: sem isso o roteador exigiria todas as chaves em toda
/// navegação, e navegar para a lista recém-aberta deixaria de ser `search: {}`.

export const DEFAULT_PAGE_SIZE = 25;

export const clientsViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    activity: choicesSearch(['ACTIVE', 'INACTIVE']),
    kind: z.enum(['ALL', 'PERSON']).optional().catch(undefined),
    access: choicesSearch(['LINKED', 'UNLINKED']),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z.enum(['displayName', 'createdAt', 'kind']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type ClientsView = {
  search: string;
  activity: Array<Exclude<ClientActivityFilter, 'ALL'>>;
  kind: ClientKind | 'ALL';
  access: Array<Exclude<ClientAccessFilter, 'ALL'>>;
  /// O recorte de dias sobre a data de cadastro. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  sort: ClientSort;
  direction: ClientDirection;
  limit: number;
  offset: number;
};

export const clientsViewDefaults: ClientsView = {
  search: '',
  activity: [],
  kind: 'ALL',
  access: [],
  from: null,
  to: null,
  sort: 'displayName',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

export const servicesViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    currency: z.enum(['ALL', 'BRL', 'USD', 'ARS']).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z
      .enum(['displayName', 'durationMinutes', 'basePriceCents', 'createdAt'])
      .optional()
      .catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    service: z.string().uuid().optional().catch(undefined),
  })
  .partial();

export type ServicesView = {
  search: string;
  activity: ServiceActivityFilter;
  currency: Currency | 'ALL';
  /// O recorte de dias sobre a data de cadastro. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  sort: ServiceSort;
  direction: ServiceDirection;
  limit: number;
  offset: number;
  service?: string | undefined;
};

export const servicesViewDefaults: ServicesView = {
  search: '',
  activity: 'ALL',
  currency: 'ALL',
  from: null,
  to: null,
  sort: 'displayName',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// Vendas tem duas páginas com a mesma URL de recorte: o histórico (`/sales`) e a Nova venda
/// (`/sales/new`), onde a busca procura pacote e o atalho "Vender" traz o item (ADR 0055).
export const salesViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    status: z.enum(['ALL', 'CONFIRMED', 'CANCELED']).optional().catch(undefined),
    method: z.enum(['ALL', 'CASH', 'PIX', 'CARD', 'OTHER', 'CREDIT']).optional().catch(undefined),
    payment: z.enum(['ALL', 'OUTSTANDING', 'AWAITING']).optional().catch(undefined),
    from: z.iso.date().optional().catch(undefined),
    to: z.iso.date().optional().catch(undefined),
    /// O item que o atalho "Vender" de Serviços ou de Produtos põe na venda: `SERVICE:<id>` ou
    /// `PRODUCT:<id da apresentação>`. Entra uma vez na venda e sai da URL.
    sell: z
      .string()
      .regex(/^(SERVICE|PRODUCT):[0-9a-f-]{36}$/u)
      .optional()
      .catch(undefined),
    /// Nova venda (ADR 0059): o tipo que o catálogo mostra, cards ou lista, e o atendimento de
    /// onde a venda veio.
    kind: z.enum(['TOP', 'ALL', 'SERVICE', 'PRODUCT', 'PACKAGE']).optional().catch(undefined),
    display: z.enum(['cards', 'list']).optional().catch(undefined),
    appointment: z.uuid().optional().catch(undefined),
    sort: z.enum(['number', 'date', 'client', 'unit', 'total']).optional().catch(undefined),
    direction: z.enum(['asc', 'desc']).optional().catch(undefined),
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type SalesCatalogKind = 'TOP' | 'ALL' | 'SERVICE' | 'PRODUCT' | 'PACKAGE';
export type SalesView = {
  search: string;
  status: 'ALL' | 'CONFIRMED' | 'CANCELED';
  method: 'ALL' | 'CASH' | 'PIX' | 'CARD' | 'OTHER' | 'CREDIT';
  /// "A receber" sobra valor em aberto; "Aguardando" há pagamento a confirmar (ADR 0053).
  payment: 'ALL' | 'OUTSTANDING' | 'AWAITING';
  from: CivilDate | null;
  to: CivilDate | null;
  sell?: string | undefined;
  kind: SalesCatalogKind;
  display: 'cards' | 'list';
  appointment?: string | undefined;
  sort?: 'number' | 'date' | 'client' | 'unit' | 'total' | undefined;
  direction?: 'asc' | 'desc' | undefined;
  limit: number;
  offset: number;
};
export const salesViewDefaults: SalesView = {
  search: '',
  status: 'ALL',
  method: 'ALL',
  payment: 'ALL',
  from: null,
  to: null,
  kind: 'ALL',
  display: 'cards',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};
/// Itens por página do catálogo da Nova venda: os cards fecham linhas de 2, 3 ou 4 colunas; a
/// lista segue as quantidades do resto do sistema (ADR 0059).
export const SALE_CATALOG_PAGE_SIZES = { cards: [12, 24, 48], list: [10, 25, 50] } as const;
export const newSaleViewDefaults: SalesView = { ...salesViewDefaults, limit: 12 };

/// Pacotes e Promoções, no Catálogo: a situação pelas datas (arquivado só no pacote).
export const salesTermsViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    term: z.enum(['ACTIVE', 'SCHEDULED', 'ENDED', 'ARCHIVED']).optional().catch(undefined),
    sort: z.enum(['name', 'date', 'price']).optional().catch(undefined),
    direction: z.enum(['asc', 'desc']).optional().catch(undefined),
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type SalesTermsView = {
  search: string;
  term: 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'ARCHIVED';
  sort?: 'name' | 'date' | 'price' | undefined;
  direction?: 'asc' | 'desc' | undefined;
  limit: number;
  offset: number;
};
export const salesTermsViewDefaults: SalesTermsView = {
  search: '',
  term: 'ACTIVE',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// O Estoque tem três listas na mesma tela, e a aba escolhida é posição navegável como qualquer
/// outra: quem manda o link do local que acabou de cadastrar não manda o saldo. O cadastro de
/// produto mora no Catálogo (ADR 0055).
export const inventoryViewSchema = z
  .object({
    tab: z.enum(['balances', 'movements', 'locations']).optional().catch(undefined),
    /// O recorte dos movimentos mora ao lado do saldo: é a mesma tela, e um parâmetro por aba
    /// deixaria a URL ilegível.
    movementKind: z
      .enum(['ALL', 'ENTRY', 'CONSUMPTION', 'LOSS', 'ADJUSTMENT', 'TRANSFER'])
      .optional()
      .catch(undefined),
    flow: z.enum(['ALL', 'IN', 'OUT']).optional().catch(undefined),
    movementSort: z.enum(['occurredAt', 'quantity']).optional().catch(undefined),
    alert: z.enum(['ALL', 'ONLY']).optional().catch(undefined),
    location: z.string().uuid().optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type InventoryView = {
  tab: 'balances' | 'movements' | 'locations';
  movementKind: 'ALL' | InventoryMovementKind;
  flow: 'ALL' | InventoryMovementFlow;
  movementSort: InventoryMovementSort;
  /// "Só o que precisa de compra" é a pergunta que a tela faz todo dia.
  alert: 'ALL' | 'ONLY';
  location: string;
  search: string;
  activity: InventoryActivityFilter;
  /// O recorte de dias dos movimentos. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  direction: InventoryDirection;
  limit: number;
  offset: number;
};

export const inventoryViewDefaults: InventoryView = {
  /// Abre no saldo: "quanto eu tenho" é a pergunta que traz alguém ao Estoque.
  tab: 'balances',
  movementKind: 'ALL',
  flow: 'ALL',
  movementSort: 'occurredAt',
  alert: 'ALL',
  location: '',
  search: '',
  activity: 'ALL',
  from: null,
  to: null,
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// Produtos, no Catálogo: o cadastro continua do Estoque, só a página saiu dele (ADR 0055).
export const productsViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    lotTracking: z.enum(['ALL', 'NONE', 'REQUIRED']).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z.enum(['displayName', 'sku', 'createdAt']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    product: z.string().uuid().optional().catch(undefined),
  })
  .partial();

export type ProductsView = {
  search: string;
  activity: InventoryActivityFilter;
  lotTracking: InventoryLotTrackingFilter;
  /// O recorte de dias sobre a data de cadastro. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  sort: InventoryProductSort;
  direction: InventoryDirection;
  limit: number;
  offset: number;
  product?: string | undefined;
};

export const productsViewDefaults: ProductsView = {
  search: '',
  activity: 'ALL',
  lotTracking: 'ALL',
  from: null,
  to: null,
  sort: 'displayName',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// Compras tem duas listas na mesma tela, e a aba escolhida é posição navegável como qualquer
/// outra: quem manda o link da oferta que acabou de cadastrar não manda a lista de fornecedores.
export const purchasingViewSchema = z
  .object({
    tab: z.enum(['offerings', 'suppliers', 'orders', 'receipts']).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    supplierKind: z.enum(['ALL', 'PERSON', 'COMPANY']).optional().catch(undefined),
    offeringKind: z.enum(['ALL', 'PRODUCT', 'SERVICE']).optional().catch(undefined),
    supplier: z.string().uuid().optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z.enum(['displayName', 'createdAt']).optional().catch(undefined),
    offeringSort: z
      .enum(['displayName', 'supplierDisplayName', 'unitPriceCents'])
      .optional()
      .catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    /// Qual cadastro está aberto em diálogo. São dois campos porque são dois diálogos diferentes.
    editSupplier: z.string().uuid().optional().catch(undefined),
    offering: z.string().uuid().optional().catch(undefined),
    orderStatus: z.enum(['OPEN', 'COMPLETED', 'CANCELED']).optional().catch(undefined),
  })
  .partial();

export type PurchasingView = {
  tab: 'offerings' | 'suppliers' | 'orders' | 'receipts';
  search: string;
  activity: PurchasingActivityFilter;
  supplierKind: PurchasingKindFilter;
  offeringKind: PurchasingOfferingKindFilter;
  supplier: string;
  /// O recorte de dias sobre a data de cadastro do fornecedor. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  sort: SupplierSort;
  offeringSort: SupplierOfferingSort;
  direction: PurchasingDirection;
  limit: number;
  offset: number;
  editSupplier?: string | undefined;
  offering?: string | undefined;
  orderStatus?: 'OPEN' | 'COMPLETED' | 'CANCELED' | undefined;
};

export const purchasingViewDefaults: PurchasingView = {
  /// Abre nas ofertas: "por quanto eu compro isso" é a pergunta que traz alguém a Compras.
  tab: 'offerings',
  search: '',
  activity: 'ALL',
  supplierKind: 'ALL',
  offeringKind: 'ALL',
  supplier: '',
  from: null,
  to: null,
  sort: 'displayName',
  offeringSort: 'displayName',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

export const protocolsViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    service: z.string().optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z.enum(['displayName', 'serviceDisplayName', 'createdAt']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    protocol: z.string().uuid().optional().catch(undefined),
  })
  .partial();

export type ProtocolsView = {
  search: string;
  activity: ProtocolActivityFilter;
  service: string;
  /// O recorte de dias sobre a data de cadastro. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  sort: ProtocolSort;
  direction: ProtocolDirection;
  limit: number;
  offset: number;
  protocol?: string | undefined;
};

export const protocolsViewDefaults: ProtocolsView = {
  search: '',
  activity: 'ALL',
  service: 'ALL',
  from: null,
  to: null,
  sort: 'displayName',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// A lista da operação: o recorte de dias vale sobre o atendimento, que é a data que interessa a
/// quem atende. O diálogo aberto também é posição navegável (ADR 0009).
export const protocolApplicationsViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    status: z.enum(['ALL', 'PLANNED', 'APPLIED']).optional().catch(undefined),
    protocol: z.string().optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z
      .enum(['appointmentStartsAt', 'clientDisplayName', 'createdAt'])
      .optional()
      .catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    application: z.string().uuid().optional().catch(undefined),
  })
  .partial();

export type ProtocolApplicationsView = {
  search: string;
  status: 'ALL' | 'PLANNED' | 'APPLIED';
  /// O ID do protocolo, ou `ALL` para não filtrar.
  protocol: string;
  /// O recorte de dias sobre o atendimento. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  sort: 'appointmentStartsAt' | 'clientDisplayName' | 'createdAt';
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
  application?: string | undefined;
};

export const protocolApplicationsViewDefaults: ProtocolApplicationsView = {
  search: '',
  status: 'ALL',
  protocol: 'ALL',
  from: null,
  to: null,
  sort: 'appointmentStartsAt',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// O consumo é lido pelo atendimento: o recorte de dias vale sobre ele, e a aplicação aberta é
/// posição navegável (ADR 0009).
/// A página do cliente (ADR 0030): a aba e o recorte da lista de atendimentos dele.
export const clientPageViewSchema = z
  .object({
    tab: z.enum(['profile', 'appointments', 'records', 'balances']).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    /// O Prontuário: a seção, a ficha aberta (pelo modelo), a versão e a comparação (ADR 0038).
    section: z.enum(['timeline', 'records']).optional().catch(undefined),
    record: z.string().uuid().optional().catch(undefined),
    version: z.coerce.number().int().min(1).optional().catch(undefined),
    compare: z.coerce.number().int().min(1).optional().catch(undefined),
    edit: z.boolean().optional().catch(undefined),
    timeline: z
      .enum(['all', 'sessions', 'records', 'occurrences', 'alerts'])
      .optional()
      .catch(undefined),
    full: z.boolean().optional().catch(undefined),
    status: z
      .enum(['ALL', 'UPCOMING', 'TO_CLOSE', 'COMPLETED', 'CANCELLED', 'NO_SHOW'])
      .optional()
      .catch(undefined),
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type ClientAppointmentsFilter =
  'ALL' | 'UPCOMING' | 'TO_CLOSE' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

export type ClientPageView = {
  tab: 'profile' | 'appointments' | 'records' | 'balances';
  search: string;
  status: ClientAppointmentsFilter;
  limit: number;
  offset: number;
  section: 'timeline' | 'records';
  record?: string | undefined;
  version?: number | undefined;
  compare?: number | undefined;
  edit: boolean;
  timeline: 'all' | 'sessions' | 'records' | 'occurrences' | 'alerts';
  full: boolean;
};

export const clientPageViewDefaults: ClientPageView = {
  tab: 'appointments',
  search: '',
  status: 'ALL',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  section: 'timeline',
  edit: false,
  timeline: 'all',
  full: false,
};

export const recordTemplatesViewSchema = z
  .object({
    template: z.string().uuid().optional().catch(undefined),
    mode: z.enum(['edit', 'preview']).optional().catch(undefined),
  })
  .partial();

export type RecordTemplatesView = {
  template?: string | undefined;
  mode: 'edit' | 'preview';
};

export const recordTemplatesViewDefaults: RecordTemplatesView = { mode: 'edit' };

export const materialUsageViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    appointment: z.string().uuid().optional().catch(undefined),
  })
  .partial();

export type MaterialUsageView = {
  search: string;
  /// O recorte de dias sobre o atendimento. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  limit: number;
  offset: number;
  appointment?: string | undefined;
};

export const materialUsageViewDefaults: MaterialUsageView = {
  search: '',
  from: null,
  to: null,
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

export const materialSufficiencyViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    state: z.enum(['ALL', 'SUFFICIENT', 'INSUFFICIENT']).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z
      .enum(['productDisplayName', 'currentQuantity', 'plannedQuantity', 'projectedQuantity'])
      .optional()
      .catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type MaterialSufficiencyView = {
  search: string;
  state: 'ALL' | 'SUFFICIENT' | 'INSUFFICIENT';
  from: CivilDate | null;
  to: CivilDate | null;
  sort: 'productDisplayName' | 'currentQuantity' | 'plannedQuantity' | 'projectedQuantity';
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
};

export const materialSufficiencyViewDefaults: MaterialSufficiencyView = {
  search: '',
  state: 'ALL',
  from: null,
  to: null,
  sort: 'productDisplayName',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// O extrato é sempre de uma conta, e a conta escolhida é posição navegável: um endereço colado
/// para outra pessoa precisa abrir o mesmo extrato, do mesmo período.
export const financeViewSchema = z
  .object({
    tab: z
      .enum(['statement', 'accounts', 'reconciliation', 'rules', 'closing'])
      .optional()
      .catch(undefined),
    account: z.string().uuid().optional().catch(undefined),
    flow: choicesSearch(['INFLOW', 'OUTFLOW']),
    status: choicesSearch(['ACTIVE', 'CANCELED']),
    search: z.string().optional().catch(undefined),
    sort: z.enum(['occurredOn', 'amount', 'createdAt']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    accountSearch: z.string().optional().catch(undefined),
    activity: choicesSearch(['ACTIVE', 'INACTIVE']),
    kind: choicesSearch(['BANK', 'CASH', 'ACQUIRER']),
    currency: choicesSearch(['BRL', 'USD', 'ARS']),
    /// "PENDING" era a situação "A conciliar" do filtro único: sem vínculo mais conciliados.
    state: choicesSearch(['UNLINKED', 'SETTLED', 'IGNORED'], {
      PENDING: ['UNLINKED', 'SETTLED'],
      ALL: ['UNLINKED', 'SETTLED', 'IGNORED'],
    }),
    reconciliationAccount: z.array(z.string().uuid()).max(50).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
  })
  .partial();

export type FinanceView = {
  tab: 'statement' | 'accounts' | 'reconciliation' | 'rules' | 'closing';
  account?: string | undefined;
  /// Os filtros de escolha múltipla são listas; vazia é "todos".
  flow: FinanceMovementDirection[];
  status: FinanceMovementStatus[];
  search: string;
  sort: FinanceMovementSort;
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
  accountSearch: string;
  activity: FinanceAccountActivity[];
  kind: FinanceAccountKind[];
  currency: Currency[];
  state: ReconciliationState[];
  /// As contas financeiras da conciliação. O extrato mostra uma conta só (`account`).
  reconciliationAccount: string[];
  /// O recorte de dias da conciliação. Ausente é o mês de trabalho da barra; presente, manda nele —
  /// e por isso mora na URL, como todo recorte que alguém pode querer mandar para outra pessoa.
  from?: CivilDate | undefined;
  to?: CivilDate | undefined;
};

export const financeViewDefaults: FinanceView = {
  tab: 'statement',
  flow: [],
  status: [],
  search: '',
  sort: 'occurredOn',
  direction: 'desc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  accountSearch: '',
  activity: [],
  kind: [],
  currency: [],
  state: ['UNLINKED', 'SETTLED'],
  reconciliationAccount: [],
};

export const payablesViewSchema = z
  .object({
    origin: z.array(z.string().min(1).max(63)).max(20).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    /// Links antigos continuam válidos: OPEN combina A vencer e Vencidas; ALL limpa o recorte.
    status: choicesSearch(['UPCOMING', 'OVERDUE', 'PAID', 'CANCELED'], {
      OPEN: ['UPCOMING', 'OVERDUE'],
    }),
    counterparty: z.array(z.string().min(1).max(160)).max(50).optional().catch(undefined),
    account: z.array(z.string().uuid()).max(50).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    periodBy: z.enum(['dueOn', 'competenceOn']).optional().catch(undefined),
    sort: z
      .enum(['dueOn', 'competenceOn', 'amount', 'description', 'counterpartyName'])
      .optional()
      .catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();
/// A mesma visão serve Despesas e Receitas: o sentido é da tela (rota), não um filtro da URL.
/// Os filtros de escolha múltipla são listas; vazia é "todas".
export type PayablesView = {
  origin: string[];
  search: string;
  status: FinancialObligationStatusChoice[];
  /// Nomes exatos da contraparte.
  counterparty: string[];
  account: string[];
  /// O recorte de dias. Nulo é o mês de trabalho da barra; presente, manda nele — e vale sobre a
  /// mesma data que `periodBy` escolhe, para o recorte e o mês nunca falarem de datas diferentes.
  from: CivilDate | null;
  to: CivilDate | null;
  periodBy: 'dueOn' | 'competenceOn';
  sort: FinancialObligationSort;
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
};
/// Quem abre Despesas quer saber o que falta pagar: o padrão é Em aberto (A vencer e Vencidas).
export const payablesViewDefaults: PayablesView = {
  origin: [],
  search: '',
  status: ['UPCOMING', 'OVERDUE'],
  counterparty: [],
  account: [],
  from: null,
  to: null,
  periodBy: 'dueOn',
  sort: 'dueOn',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

export const schedulingViewSchema = z
  .object({
    tab: z.enum(['appointments', 'calendar', 'rooms', 'blocks']).optional().catch(undefined),
    view: z.enum(['month', 'week', 'day']).optional().catch(undefined),
    professionals: z.array(z.string().uuid()).optional().catch(undefined),
    rooms: z
      .array(z.union([z.string().uuid(), z.literal('none')]))
      .optional()
      .catch(undefined),
    cancelled: z.boolean().optional().catch(undefined),
    date: civilDateSearch,
    to: civilDateSearch,
    search: z.string().optional().catch(undefined),
    status: z
      .enum(['ALL', 'SCHEDULED', 'CONFIRMED', 'COMPLETED', 'CANCELLED', 'NO_SHOW'])
      .optional()
      .catch(undefined),
    professional: z.string().uuid().optional().catch(undefined),
    room: z.string().uuid().optional().catch(undefined),
    sort: z
      .enum(['startsAt', 'clientDisplayName', 'serviceDisplayName'])
      .optional()
      .catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    appointment: z.string().uuid().optional().catch(undefined),
    /// Serviço já vendido a agendar: abre o Novo agendamento preenchido (ADR 0046).
    book: z.string().uuid().optional().catch(undefined),
    resourceSearch: z.string().optional().catch(undefined),
    resourceActivity: choicesSearch(['ACTIVE', 'INACTIVE']),
    resourceSort: z.enum(['displayName', 'createdAt']).optional().catch(undefined),
    resourceDirection: sortDirectionSearch,
    resource: z.string().uuid().optional().catch(undefined),
    blockSearch: z.string().optional().catch(undefined),
    blockActivity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    blockResource: z.string().uuid().optional().catch(undefined),
    blockSort: z.enum(['startsAt', 'resourceDisplayName', 'createdAt']).optional().catch(undefined),
    blockDirection: sortDirectionSearch,
  })
  .partial();

export type SchedulingView = {
  tab: 'appointments' | 'calendar' | 'rooms' | 'blocks';
  /// Visão do calendário. Mês, semana ou dia, como nos calendários de mercado.
  view: 'month' | 'week' | 'day';
  /// Filtros do calendário. `undefined` é "todos"; uma lista, mesmo vazia, é escolha de quem
  /// filtrou. Em salas, `none` quer dizer "sem sala".
  professionals: string[] | undefined;
  rooms: string[] | undefined;
  cancelled: boolean;
  /// O primeiro dia da agenda à vista; nulo é hoje. `to` é o último: ausente, a agenda é de um dia
  /// só, que é como ela sempre foi aberta.
  date: CivilDate | null;
  to: CivilDate | null;
  search: string;
  status: AppointmentStatusFilter;
  professional: string;
  room: string;
  sort: AppointmentSort;
  direction: SchedulingDirection;
  limit: number;
  offset: number;
  appointment: string;
  book: string;
  resourceSearch: string;
  resourceActivity: Array<'ACTIVE' | 'INACTIVE'>;
  resourceSort: 'displayName' | 'createdAt';
  resourceDirection: SchedulingDirection;
  resource: string;
  blockSearch: string;
  blockActivity: SchedulingActivityFilter;
  blockResource: string;
  blockSort: ScheduleBlockSort;
  blockDirection: SchedulingDirection;
};

export const schedulingViewDefaults: SchedulingView = {
  tab: 'calendar',
  /// A agenda abre sempre no dia de hoje: é a pergunta de quem chega na recepção.
  view: 'day',
  professionals: undefined,
  rooms: undefined,
  cancelled: false,
  date: null,
  to: null,
  search: '',
  status: 'ALL',
  professional: '',
  room: '',
  sort: 'startsAt',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  appointment: '',
  book: '',
  resourceSearch: '',
  resourceActivity: [],
  resourceSort: 'displayName',
  resourceDirection: 'asc',
  resource: '',
  blockSearch: '',
  blockActivity: 'ALL',
  blockResource: '',
  blockSort: 'startsAt',
  blockDirection: 'asc',
};

/// Profissionais, em tela própria (ADR 0083). `professional` é o cadastro aberto.
export const professionalsViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    activity: choicesSearch(['ACTIVE', 'INACTIVE']),
    sort: z.enum(['displayName', 'createdAt']).optional().catch(undefined),
    direction: sortDirectionSearch,
    professional: z.string().uuid().optional().catch(undefined),
  })
  .partial();

export type ProfessionalsView = {
  search: string;
  activity: Array<'ACTIVE' | 'INACTIVE'>;
  sort: 'displayName' | 'createdAt';
  direction: SchedulingDirection;
  professional: string;
};

export const professionalsViewDefaults: ProfessionalsView = {
  search: '',
  activity: [],
  sort: 'displayName',
  direction: 'asc',
  professional: '',
};

/// Auditoria. `entityTypes` indefinido = nenhuma tela foi mexida, então valem todas; uma lista
/// (mesmo vazia) é escolha explícita de quem filtrou, e tem de sobreviver até o servidor.
///
/// As duas datas são duas, e não períodos prontos: "de 1º a 15 de agosto" é a pergunta que se faz a
/// uma auditoria, e nenhum atalho de 24h/7d/30d responde a ela.
export const auditViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    action: z.string().optional().catch(undefined),
    actor: z.string().optional().catch(undefined),
    entityTypes: z.array(z.string()).optional().catch(undefined),
    origin: z.enum(['ALL', 'USER', 'ROOT', 'SYSTEM']).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z.enum(['createdAt', 'actor', 'action']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

/// `type`, e não `interface`: só o primeiro tem assinatura de índice implícita, e é dela que o
/// `useViewState` precisa para andar sobre as chaves do recorte sem saber quais são.
export type AuditView = {
  search: string;
  action: string;
  actor: string;
  entityTypes: string[] | undefined;
  origin: AuditOriginFilter;
  from: CivilDate | null;
  to: CivilDate | null;
  sort: AuditSort;
  direction: AuditDirection;
  limit: number;
  offset: number;
};

export const auditViewDefaults: AuditView = {
  search: '',
  action: 'ALL',
  actor: 'ALL',
  entityTypes: undefined,
  origin: 'ALL',
  from: null,
  to: null,
  sort: 'createdAt',
  direction: 'desc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// Caixa de saída — a mesma em `/communication/outbox` e em
/// `/companies/:key/communication/outbox`.
export const outboxViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    status: choicesSearch(['PENDING', 'SENT', 'FAILED']),
    event: stringChoicesSearch(z.string().min(1).max(120)),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z.enum(['createdAt', 'recipient', 'status']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type OutboxView = {
  search: string;
  status: EmailMessageStatus[];
  event: string[];
  from: CivilDate | null;
  to: CivilDate | null;
  sort: EmailMessageSort;
  direction: AuditDirection;
  limit: number;
  offset: number;
};

export const outboxViewDefaults: OutboxView = {
  search: '',
  status: [],
  event: [],
  from: null,
  to: null,
  sort: 'createdAt',
  direction: 'desc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

/// Sócios. A lista não pagina — são poucos por empresa —, e por isso não tem `limit` nem `offset`.
export const partnersViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    sort: z.enum(['displayName', 'createdAt']).optional().catch(undefined),
    direction: sortDirectionSearch,
  })
  .partial();

export type PartnersView = {
  search: string;
  activity: PartnerActivityFilter;
  sort: PartnerSort;
  direction: PartnerDirection;
};

export const partnersViewDefaults: PartnersView = {
  search: '',
  activity: 'ALL',
  sort: 'displayName',
  direction: 'asc',
};

/// Busca e atividade de escolha única no console, base da lista de empresas.
/// Planos estende este esquema com atividade de escolha múltipla abaixo.
///
/// O modo de exibição das empresas (cards ou lista) fica **de fora**: é preferência de quem olha,
/// não recorte do que está sendo olhado, e por isso continua no `localStorage`. Mandar um link não
/// deve impor a forma de ver de quem mandou.
export const activityViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
  })
  .partial();

export type ActivityView = {
  search: string;
  activity: 'ALL' | 'ACTIVE' | 'INACTIVE';
};

export const activityViewDefaults: ActivityView = { search: '', activity: 'ALL' };

/// Planos: situação aceita várias escolhas; links antigos de escolha única continuam válidos.
export const plansViewSchema = activityViewSchema
  .extend({ activity: choicesSearch(['ACTIVE', 'INACTIVE']) })
  .partial();
export type PlansView = { search: string; activity: Array<'ACTIVE' | 'INACTIVE'> };
export const plansViewDefaults: PlansView = { search: '', activity: [] };

/// A lista de empresas tem um parâmetro a mais: qual contratação está aberta.
///
/// **Ela não virou `/companies/:key/subscription`, como o mapa deste ADR previa.** Depois que a
/// casca da empresa passou a morar nesse caminho, abrir aquele endereço faria o root **entrar na
/// empresa** — troca de contexto, gravada na trilha — só por alguém ter clicado num link. A
/// contratação é ação de console sobre uma empresa em que não se entra, e por isso o endereço é a
/// lista mais o registro aberto.
export const companiesViewSchema = activityViewSchema.extend({
  subscription: z.string().optional().catch(undefined),
});

export type CompaniesView = ActivityView & { subscription: string };

export const companiesViewDefaults: CompaniesView = { ...activityViewDefaults, subscription: '' };

/// Modelos de e-mail: qual modelo está aberto. O mesmo contrato serve à tela do console e à da
/// empresa, que são a mesma tela pendurada em duas rotas.
///
/// **É parâmetro de busca, e não `/templates/:event` como o mapa deste ADR previa.** Qual é o
/// primeiro modelo só se sabe depois que a lista chega do servidor; com caminho, o endereço nu
/// precisaria de um redirecionamento que espera dados para saber para onde ir. Ausente quer dizer
/// "o primeiro", e isso é uma resposta que não depende de ninguém.
export const templatesViewSchema = z
  .object({ event: z.string().optional().catch(undefined) })
  .partial();

export type TemplatesView = { event: string };

export const templatesViewDefaults: TemplatesView = { event: '' };

/// Pessoas e acessos. As duas abas — Pessoas e Perfis — são a mesma entidade vista de outro jeito,
/// e por isso continuam abas de um endereço só, e não dois endereços. A aba escolhida entra na URL
/// junto com o filtro, senão recarregar devolveria a pessoa para a aba errada.
///
/// Os dois painéis compartilham o nome `search` porque só um está montado por vez; `status` é de
/// Pessoas e `kind` é de Perfis. Trocar de aba limpa os três: o recorte de um painel não quer dizer
/// nada no outro.
export const accessViewSchema = z
  .object({
    tab: z.enum(['members', 'roles']).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    status: z.enum(['ALL', 'INVITED', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
    kind: z.enum(['ALL', 'PROTECTED', 'CUSTOM']).optional().catch(undefined),
  })
  .partial();

export type AccessView = {
  tab: 'members' | 'roles';
  search: string;
  status: 'ALL' | 'INVITED' | 'ACTIVE' | 'INACTIVE';
  kind: 'ALL' | 'PROTECTED' | 'CUSTOM';
};

export const accessViewDefaults: AccessView = {
  tab: 'members',
  search: '',
  status: 'ALL',
  kind: 'ALL',
};

export const platformAccessViewSchema = z
  .object({
    tab: z.enum(['accesses', 'roles']).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    activity: z.enum(['ALL', 'ACTIVE', 'INACTIVE']).optional().catch(undefined),
  })
  .partial();

export type PlatformAccessView = {
  tab: 'accesses' | 'roles';
  search: string;
  activity: 'ALL' | 'ACTIVE' | 'INACTIVE';
};

export const platformAccessViewDefaults: PlatformAccessView = {
  tab: 'accesses',
  search: '',
  activity: 'ALL',
};

/// Aportes. Também sem paginação: a lista de aportes de uma empresa cabe numa tela.
export const contributionsViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    type: z.enum(['ALL', 'LOAN', 'OWN_CASH', 'COMPANY_FUNDS']).optional().catch(undefined),
    status: z
      .enum(['ALL', 'NOT_REPAYABLE', 'PENDING', 'PARTIAL', 'REPAID'])
      .optional()
      .catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    sort: z.enum(['contributedOn', 'amount', 'partner', 'outstanding']).optional().catch(undefined),
    direction: sortDirectionSearch,
  })
  .partial();

export type ContributionsView = {
  search: string;
  type: ContributionTypeFilter;
  status: ContributionStatusFilter;
  /// O recorte de dias sobre a data do aporte. Nulo é a lista inteira.
  from: CivilDate | null;
  to: CivilDate | null;
  sort: ContributionSort;
  direction: ContributionDirection;
};

export const contributionsViewDefaults: ContributionsView = {
  search: '',
  type: 'ALL',
  status: 'ALL',
  from: null,
  to: null,
  sort: 'contributedOn',
  direction: 'desc',
};

/// Lançamentos. Situação, forma e "Pago por" aceitam várias opções. Indefinido = ninguém mexeu,
/// valem todas; uma lista (mesmo vazia) é escolha explícita e tem de sobreviver até o servidor.
/// "Pago por" mistura tipos de pagador e sócios (`COMPANY`, `THIRD_PARTY`, `partner:<id>`), porque é
/// um filtro só na tela.
export const entriesViewSchema = z
  .object({
    search: z.string().optional().catch(undefined),
    statuses: z
      .array(
        z.enum([
          'TO_REIMBURSE',
          'PARTIALLY_REIMBURSED',
          'REIMBURSED',
          'PAID',
          'PARTIALLY_PAID',
          'PAYMENT_PENDING',
        ]),
      )
      .optional()
      .catch(undefined),
    methods: z
      .array(z.enum(['PIX', 'DEBIT', 'CASH', 'BANK_SLIP', 'CREDIT']))
      .optional()
      .catch(undefined),
    payers: z.array(z.string()).optional().catch(undefined),
    from: civilDateSearch,
    to: civilDateSearch,
    periodBy: z.enum(['purchasedOn', 'dueOn', 'settledOn']).optional().catch(undefined),
    sort: z.enum(['purchasedOn', 'dueOn', 'amount', 'description']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
    /// A ficha aberta e, dentro dela, comprovantes ou boletos. São três diálogos que mostram um
    /// registro, e por isso ganham endereço: dá para mandar o link, o gesto de voltar fecha, e
    /// recarregar não perde a ficha.
    ///
    /// **São parâmetros, e não `/entries/:entryId` como o mapa deste ADR previa.** A ficha abre o
    /// formulário de edição, a baixa de parcela e a exclusão — três diálogos que continuam sem
    /// endereço, porque são passos de uma ação. Esse estado é da tela da lista; com a ficha em rota
    /// irmã, ele teria de sair da tela por um contexto criado só para atender ao formato do
    /// endereço. O endereço ficaria mais bonito e o código, pior.
    entry: z.string().optional().catch(undefined),
    entryTab: z.enum(['receipts', 'slips']).optional().catch(undefined),
  })
  .partial();

export type EntriesView = {
  search: string;
  statuses: EntryStatus[] | undefined;
  methods: PaymentMethod[] | undefined;
  payers: string[] | undefined;
  /// O recorte de dias e sobre qual data ele vale — a compra, o vencimento ou a baixa. São
  /// perguntas diferentes, e a lista responde a uma por vez.
  from: CivilDate | null;
  to: CivilDate | null;
  periodBy: EntryPeriodBy;
  sort: EntrySort;
  direction: EntryDirection;
  limit: number;
  offset: number;
  entry: string;
  entryTab: 'receipts' | 'slips' | '';
};

export const entriesViewDefaults: EntriesView = {
  search: '',
  statuses: undefined,
  methods: undefined,
  payers: undefined,
  from: null,
  to: null,
  periodBy: 'purchasedOn',
  sort: 'purchasedOn',
  direction: 'desc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  entry: '',
  entryTab: '',
};

/// A fila de classificação da Contabilidade gerencial (ADR 0061). A origem é escolha obrigatória:
/// a fila nunca soma o extrato com os compromissos, que contariam o mesmo gasto duas vezes.
export const classificationViewSchema = z
  .object({
    kind: z.enum(['MOVEMENTS', 'OBLIGATIONS', 'RECURRENCES']).optional().catch(undefined),
    filter: z.enum(['PENDING', 'CLASSIFIED', 'ALL']).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    sort: z.enum(['date', 'description', 'amount']).optional().catch(undefined),
    direction: sortDirectionSearch,
    limit: pageSizeSearch,
    offset: pageOffsetSearch,
  })
  .partial();

export type ClassificationView = {
  kind: 'MOVEMENTS' | 'OBLIGATIONS' | 'RECURRENCES';
  filter: 'PENDING' | 'CLASSIFIED' | 'ALL';
  search: string;
  sort: 'date' | 'description' | 'amount';
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
};

export const classificationViewDefaults: ClassificationView = {
  kind: 'MOVEMENTS',
  filter: 'PENDING',
  search: '',
  sort: 'date',
  direction: 'asc',
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

export const classificationRulesViewSchema = z
  .object({
    filter: z.enum(['ALL', 'VALID', 'SUSPENDED']).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
  })
  .partial();
export type ClassificationRulesView = { filter: 'ALL' | 'VALID' | 'SUSPENDED'; search: string };
export const classificationRulesViewDefaults: ClassificationRulesView = {
  filter: 'ALL',
  search: '',
};

export const dreViewSchema = z
  .object({ show: z.enum(['values', 'percent']).optional().catch(undefined) })
  .partial();
export type DreView = { show: 'values' | 'percent' };
export const dreViewDefaults: DreView = { show: 'values' };

export const cashFlowViewSchema = z
  .object({
    account: z.string().uuid().optional().catch(undefined),
    mode: z.enum(['all', 'realized']).optional().catch(undefined),
  })
  .partial();
export type CashFlowView = { account: string; mode: 'all' | 'realized' };
export const cashFlowViewDefaults: CashFlowView = { account: '', mode: 'all' };
